/**
 * La comprovació de sessió en arrencar l'app, fora de React perquè es pugui provar sola.
 *
 * Regla (F3 de `plans/sessions-i-refresh-tokens.md`): els tokens **només** s'esborren quan
 * `/auth/verify` respon 401. Qualsevol altra fallada (sense xarxa, 502 d'un deploy, 429, un
 * refresc que no s'ha pogut fer) deixa la sessió com era: obrir l'app durant els ~35 s d'un
 * deploy no ha de fer tornar a entrar.
 */
import { clearRefreshPause, getAccessToken } from '@/services/api';
import type { User } from '@/services/auth.service';

export type SessionCheck =
  | { status: 'anonymous' }
  | { status: 'authenticated'; user: User }
  /** `/auth/verify` ha dit 401: la sessió no val i els tokens ja s'han esborrat. */
  | { status: 'rejected' }
  /** No s'ha pogut saber: la sessió es conserva i es pot tornar a provar. */
  | { status: 'unavailable' };

/** Un 401 amb resposta del servidor. Un error sense `response` és xarxa o refresc no disponible. */
export function isSessionRejected(error: unknown): boolean {
  return (error as { response?: { status?: number } } | null)?.response?.status === 401;
}

function storedRefreshToken(): string | null {
  return typeof localStorage === 'undefined' ? null : localStorage.getItem('refreshToken');
}

/** Per si l'emmagatzematge no parés de canviar: la comprovació no es repeteix sense fi. */
const MAX_SESSION_CHANGES = 3;

export async function checkSession(
  verify: () => Promise<User>,
  attempt = 0,
): Promise<SessionCheck> {
  if (!getAccessToken()) return { status: 'anonymous' };
  // Qui crida això ho fa en arrencar o perquè algú ha demanat un reintent («Torna-ho a
  // provar», `online`): la pausa d'un refresc fallit de fa un moment no l'ha de frenar.
  clearRefreshPause();
  // El refresh token identifica la sessió que es comprova: el refresc canvia el token
  // d'accés, però només un login (o un logout) canvia aquest.
  const checkedSession = storedRefreshToken();
  try {
    return { status: 'authenticated', user: await verify() };
  } catch (error) {
    if (!isSessionRejected(error)) return { status: 'unavailable' };

    const current = storedRefreshToken();
    if (current === checkedSession) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      return { status: 'rejected' };
    }
    // L'emmagatzematge ja no té la sessió que s'ha rebutjat. Si és buit, ja l'ha esborrada
    // el client (`api.ts`) o un logout. Si n'hi ha una altra, algú ha entrat mentre això
    // viatjava (aquí o en una altra pestanya): el 401 no parla d'ella, i es comprova la nova.
    if (current === null || attempt >= MAX_SESSION_CHANGES) return { status: 'rejected' };
    return checkSession(verify, attempt + 1);
  }
}

/**
 * Una altra pestanya ha deixat aquest navegador sense sessió? (esdeveniment `storage`, que
 * només arriba a les pestanyes que NO han fet el canvi). `key === null` és un
 * `localStorage.clear()`. Un token que canvia de valor (refresc, login) no compta.
 */
export function sessionClearedElsewhere(event: { key: string | null; newValue: string | null }): boolean {
  if (event.key === null) return true;
  return (event.key === 'accessToken' || event.key === 'refreshToken') && event.newValue === null;
}

export type ProtectedRouteView = 'content' | 'unavailable' | 'loading' | 'login';

/**
 * Què ensenya `ProtectedRoute`. «No s'ha pogut connectar» va abans del carregant perquè el
 * reintent no faci saltar la pantalla; qui ja és dins hi segueix encara que es recomprovi.
 */
export function protectedRouteView(state: {
  isAuthenticated: boolean;
  isLoading: boolean;
  connectionError: boolean;
}): ProtectedRouteView {
  if (state.isAuthenticated) return 'content';
  if (state.connectionError) return 'unavailable';
  if (state.isLoading) return 'loading';
  return 'login';
}
